import React, { useState } from 'react';
import { CheckCircle2, Loader2, PhoneCall } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import HeardAboutUsFields from '@/components/public/HeardAboutUsFields';
import { getLeadAttribution, inferProductFromPath, trackConversionEvent } from '@/lib/seoPerformanceTracker';

const formatPhone = (val) => {
  const n = val.replace(/\D/g, '').slice(0, 10);
  if (n.length < 4) return n;
  if (n.length < 7) return `(${n.slice(0, 3)}) ${n.slice(3)}`;
  return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
};

export default function CallbackRequestCard({ defaultLine = 'Commercial Auto', defaultProduct }) {
  const resolvedProduct = defaultProduct || (typeof window !== 'undefined' ? inferProductFromPath(window.location.pathname) : 'commercial_truck');
  const [form, setForm] = useState({
    first_name: '',
    phone: '',
    zip: '',
    insurance_line: defaultLine,
    insurance_product: resolvedProduct,
    self_reported_source: '',
    self_reported_query: '',
  });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone.replace(/\D/g, '').length !== 10) {
      setError('Please enter a valid 10-digit US phone number.');
      trackConversionEvent('form_error', { field: 'phone', error_type: 'validation', product: form.insurance_product });
      return;
    }
    setSaving(true);
    setError('');
    try {
      const page = typeof window !== 'undefined' ? window.location.pathname : '/contact';
      const res = await base44.functions.invoke('capturePublicLead', {
        callback_request: true,
        ...form,
        ...getLeadAttribution(),
      });
      if (!res?.data?.ok) throw new Error(res?.data?.error || 'Could not send request.');
      trackConversionEvent('callback_submit', {
        self_reported_source: form.self_reported_source || null,
        product: form.insurance_product,
        page,
      });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Could not send request.';
      setError(msg);
      trackConversionEvent('form_error', { field: 'callback_submit', error_type: 'api_error', product: form.insurance_product });
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-border bg-card p-7 text-center shadow-sm">
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
        <h3 className="mt-4 font-heading text-2xl font-bold">Callback Request Received</h3>
        <p className="mt-2 text-sm text-muted-foreground">Thank you, {form.first_name}. An InsurixPro licensed specialist will call you at {form.phone} shortly.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      toolname="request_insurance_callback"
      tooldescription="Request a fast phone callback from a licensed InsurixPro commercial trucking or auto insurance agent."
      className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8 text-left"
    >
      <input type="hidden" name="insurance_product" value={form.insurance_product} />
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-primary"><PhoneCall className="h-5 w-5" /></span>
        <div>
          <h2 className="font-heading text-2xl font-bold">Request a Fast Callback</h2>
          <p className="text-xs text-muted-foreground">Short on time? Leave your number and our agent will call you to quote by phone.</p>
        </div>
      </div>
      <div className="mt-5 grid gap-3.5 sm:grid-cols-2">
        <div>
          <Label htmlFor="cb-name">Your Name *</Label>
          <Input
            id="cb-name"
            name="first_name"
            toolparamdescription="Applicant or business contact full name"
            required
            value={form.first_name}
            onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="cb-phone">Phone Number *</Label>
          <Input
            id="cb-phone"
            name="phone"
            type="tel"
            inputMode="tel"
            toolparamdescription="10-digit US callback phone number"
            required
            value={form.phone}
            onChange={(e) => setForm((p) => ({ ...p, phone: formatPhone(e.target.value) }))}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="cb-line">Insurance Type</Label>
          <select
            id="cb-line"
            name="insurance_line"
            toolparamdescription="Type of commercial trucking, auto, or personal insurance needed"
            value={form.insurance_line}
            onChange={(e) => setForm((p) => ({ ...p, insurance_line: e.target.value }))}
            className="mt-1 flex h-9 w-full rounded-md border border-input bg-card px-3 py-1 text-sm shadow-sm"
          >
            <option value="Commercial Auto">Commercial Auto / Work Truck</option>
            <option value="Trucking & USDOT">Trucking / Semi / New Authority</option>
            <option value="Car Hauler Insurance">Car Hauler / Auto Transport</option>
            <option value="Box Truck Insurance">Box Truck / Straight Truck</option>
            <option value="Personal Auto">Personal Auto / SR-22</option>
            {!['Commercial Auto', 'Trucking & USDOT', 'Car Hauler Insurance', 'Box Truck Insurance', 'Personal Auto'].includes(form.insurance_line) && (
              <option value={form.insurance_line}>{form.insurance_line}</option>
            )}
          </select>
        </div>
        <div>
          <Label htmlFor="cb-zip">ZIP Code</Label>
          <Input
            id="cb-zip"
            name="zip"
            inputMode="numeric"
            toolparamdescription="5-digit US garaging ZIP code"
            value={form.zip}
            onChange={(e) => setForm((p) => ({ ...p, zip: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
            className="mt-1"
          />
        </div>
        <HeardAboutUsFields idPrefix="cb" source={form.self_reported_source} query={form.self_reported_query} onChange={(field, value) => setForm((p) => ({ ...p, [field]: value }))} />
      </div>
      {error && <p role="alert" className="mt-3 text-xs font-medium text-destructive">{error}</p>}
      <Button type="submit" disabled={saving} className="mt-5 w-full h-11 font-semibold">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Call Me With Quotes'}
      </Button>
    </form>
  );
}